import { useRefreshHealth } from './shellBadges';
import RefreshHealthBadge from './RefreshHealthBadge';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL as API_URL } from '../services/api';
import { shellRead } from './shellRead';   // Round 4e: the shell's one request (version rides it)
import { useAuth } from '../context/AuthContext';
import { useDevMode, setDevMode } from './devmode';
import UserMenu from './UserMenu';
import { CREATEICON, Glyph } from './glyphs';
import { useDataHealthCount } from './dataHealthCount';   // fb-185
import { MODULE_TREE } from '../config/moduleTree';

import { CREATE_SECTIONS, CREATE_PARAMS, CREATE_ROUTES, CREATE_TABS } from './createMenuModel.mjs';
const createGlyph = key => CREATEICON[key[0].toUpperCase() + key.slice(1)];

// ☰ Control & Administration — grouped: a non-clickable "Settings" header with its first-layer
// settings groups persistently listed under it (no icons), a separator, the engines/logs (with
// icons), a separator, then Changelog last.
const _adminModule = MODULE_TREE.find(group => group.admin)?.modules[0];
const _adminLabel = key => _adminModule?.sections.find(section => section.key === key)?.label || key;
const SETTINGS_KEYS = ['organisation-settings', 'finance-settings', 'documents-management', 'inventory-settings', 'operations-settings', 'system-settings', 'access-control'];   // fb-104
const ENGINE_ITEMS = [
  ['🔄', 'Data Refreshes', 'data-refreshes'],   // fb-104: first in the Engines group — below the Settings divider, above Engines
  ['🩺', 'Data Health', 'data-health'],   // fb-142 — ordered to match the LEFT MENU (Data Refreshes → Data Health → Engines → … → Sanity)
  ['🔬', 'Engines', 'engines'], ['🔩', 'Supporting Engines', 'supporting-engines'], ['🔍', 'Sanity Report', 'sanity'],
  ['🗒️', 'Audit Log', 'audit-log'], ['🧬', 'Lifecycle Log', 'lifecycle-log'], ['🌐', 'Language', 'language'],
];
// 🔍 search "Jump to" — sample cross-entity results (mock #searchPop)
const SEARCH_ITEMS = [
  ['/sales', 'Ferme El Baraka', 'Customer'], ['/purchasing', 'PO-2418 · Aliments du Nord', 'Purchase order'],
  ['/inventory', 'Feed — Starter crumble', 'Item'], ['/finance', 'JE-9042', 'Journal entry'],
];
// 🔔 notifications (mock #bellPop)
const NOTES = [
  ['var(--st-crit)', '3 invoices overdue >30 days', 'AR · 92 400 total'],
  ['var(--st-warn)', 'Feed stock below reorder', 'Inventory · 6 items'],
  ['var(--st-good)', 'PO-2411 received in full', 'Purchasing · 2h ago'],
];

// Round 1 (C1): one /version read per page load (StrictMode / a remount reused it); a failure is not kept, so the next mount retries.
// Round 4e: display only (the user menu's API line and the offline dot), so it rides the shell's one request after paint (shellRead). If that
// request fails (busy, expired session), the old unauthenticated read decides "offline", so the dot never blames the API for a shell refusal.
let _version = null;
const versionInfo = () => _version || (_version = shellRead('version')
  .catch(() => fetch(`${API_URL}/version`).then(r => (r.ok ? r.json() : Promise.reject())))
  .catch(e => { _version = null; throw e; }));

export default function GlobalBar({ onCreate }) {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();   // fb-223: i18n'd Create items (optional 4th tuple slot = i18n key)
  const [menu, setMenu] = useState(null);     // 'admin' | 'create' | 'user' | 'bell' | null
  const refreshHealth = useRefreshHealth(!!isAdmin && menu === 'admin');   // 47284b: the ☰ menu is the TOP-bar home of Data Refreshes (control-admin is not a module tab); same shared read as the left menu, visible on the burger without opening
  const dataHealthN = useDataHealthCount(!!isAdmin && menu === 'admin');   // fb-185: badge on the Data Health item of the ☰ menu (Round 1: fetched when the menu first opens, not at boot)
  const devOn = useDevMode();
  const navigate = useNavigate();
  const [pos, setPos] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [apiInfo, setApiInfo] = useState(null);
  const [offline, setOffline] = useState(false);
  const barRef = useRef(null);
  const searchBlurTimer = useRef(null);
  const searchRef = useRef(null);
  const [searchPos, setSearchPos] = useState(null);
  const openSearch = () => { clearTimeout(searchBlurTimer.current); const r = searchRef.current?.getBoundingClientRect(); if (r) setSearchPos({ left: r.left, top: r.bottom + 6, width: r.width }); setSearchOpen(true); };
  const avatarInitials = (user?.name || user?.email || 'AP').split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();

  const toggle = (name, event) => {
    if (menu === name) { setMenu(null); return; }
    const r = event.currentTarget.getBoundingClientRect();
    setPos(name === 'admin'
      ? { top: r.bottom + 6, left: r.left, right: 'auto' }
      : { top: r.bottom + 6, right: name === 'create' ? Math.max(8, Math.min(window.innerWidth - r.right, window.innerWidth - 8 - Math.min(660, window.innerWidth - 16))) : window.innerWidth - r.right, left: 'auto' });   // the 2-column Create menu never overflows the viewport on the left
    setMenu(name);
  };
  const close = () => setMenu(null);
  // arrow keys move through the grid by geometry (Right/Left = next/previous in row-major order, Down/Up = the nearest item below/above in the same column)
  const moveInGrid = e => {
    const k = e.key; if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(k)) return;
    const items = [...e.currentTarget.querySelectorAll('button[role="menuitem"]')]; const i = items.indexOf(document.activeElement); e.preventDefault();
    if (i < 0) { items[0]?.focus(); return; }
    if (k === 'ArrowRight') { items[Math.min(i + 1, items.length - 1)].focus(); return; }
    if (k === 'ArrowLeft') { items[Math.max(i - 1, 0)].focus(); return; }
    const c = items[i].getBoundingClientRect(), dir = k === 'ArrowDown' ? 1 : -1;
    const cand = items.filter(b => { const r = b.getBoundingClientRect(); return dir > 0 ? r.top > c.bottom - 2 : r.bottom < c.top + 2; })
      .sort((x, y) => { const rx = x.getBoundingClientRect(), ry = y.getBoundingClientRect(); return (Math.abs(rx.top - c.top) - Math.abs(ry.top - c.top)) || (Math.abs(rx.left - c.left) - Math.abs(ry.left - c.left)); });
    (cand[0] || items[i]).focus();
  };

  useEffect(() => {
    versionInfo()
      .then(info => { setApiInfo(info); setOffline(false); }).catch(() => setOffline(true));
  }, []);
  useEffect(() => {
    const onDown = event => { if (!barRef.current?.contains(event.target)) { close(); setSearchOpen(false); } };
    const onEsc = event => { if (event.key === 'Escape') { close(); setSearchOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, []);

  return <header ref={barRef} className="gbar">
    <button type="button" className="burger" title={t('common:chromeUi.adminModules')} aria-label={t('common:chromeUi.controlAndAdministration')} aria-expanded={menu === 'admin'} onClick={e => toggle('admin', e)}>☰</button>
    <Link className="brand" to="/home/today" aria-label={t('common:chromeUi.avipiloteHome')}><span className="glyph">🐔</span><span>{t('common:chromeUi.avipilote')}</span></Link>
    <div className="spacer" />
    <div className="search" ref={searchRef}>
      <span className="mag" aria-hidden="true">🔍</span>
      <input aria-label={t('common:chromeUi.globalSearch')} placeholder={t('common:chromeUi.searchCustomersOrdersItemsJournal')} autoComplete="off"
        onFocus={openSearch}
        onBlur={() => { searchBlurTimer.current = setTimeout(() => setSearchOpen(false), 120); }} />
      {searchOpen && searchPos && <div className="search-pop open" style={{ position: 'fixed', left: searchPos.left, top: searchPos.top, width: searchPos.width, right: 'auto', zIndex: 70 }}>
        <div className="sp-lab eyebrow">{t('common:chromeUi.jumpTo')}</div>
        {SEARCH_ITEMS.map(([to, label, kind]) => <Link key={label} to={to} onMouseDown={e => e.preventDefault()} onClick={() => setSearchOpen(false)}>{label}<span>{kind}</span></Link>)}
      </div>}
    </div>
    <div className="spacer" />
    <button type="button" className="gbtn primary" aria-expanded={menu === 'create'} onClick={e => toggle('create', e)}>{t('common:chromeUi.create')} <span style={{ opacity: 0.7 }} aria-hidden="true">▾</span></button>
    <button type="button" className="iconbtn" aria-label={t('common:chromeUi.notifications')} aria-expanded={menu === 'bell'} onClick={e => toggle('bell', e)}>🔔<span className="dot" /></button>
    <button type="button" className="avatar" title={t('common:chromeUi.account')} aria-label={t('common:chromeUi.accountMenu')} aria-expanded={menu === 'user'} onClick={e => toggle('user', e)}>{avatarInitials}</button>

    {menu === 'admin' && <nav id="adminMenu" className="menu open" style={{ minWidth: 250, ...pos }} aria-label={t('common:chromeUi.controlAndAdministration')}>
      <div className="m-lab eyebrow">{t('common:chromeUi.controlAdministration')}</div>
      <div className="menu-static"><span className="mi">⚙️</span><span>{t('common:nav.settings')}</span></div>
      {SETTINGS_KEYS.map(key => <Link className="menu-link submenu-link" key={key} to={`/control-admin/${key}`} onClick={close}>{t(`nav:s_control-admin_${key}`, _adminLabel(key))}</Link>)}
      <div className="m-sep" />
      {ENGINE_ITEMS.map(([emoji, label, key]) => <Link className="menu-link" key={key} to={`/control-admin/${key}`} onClick={close}><span className="mi">{emoji}</span>{label}{key === 'data-refreshes' && <RefreshHealthBadge health={refreshHealth} />}{key === 'data-health' && dataHealthN > 0 && <span className="badge warn" title={t('common:chromeUi.activeDataHealthWarnings')}>{dataHealthN}</span>}</Link>)}
      <div className="m-sep" />
      <Link className="menu-link" to="/control-admin/changelog" onClick={close}><span className="mi">📋</span>{t('common:nav.changelog')}</Link>
      {isAdmin && <>
        <div className="m-sep" />
        <button data-borderless="chip" type="button" className="menu-link" role="switch" aria-checked={devOn}
          onClick={() => { setDevMode(!devOn); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
          <span className="mi">🛠️</span><span style={{ flex: 1 }}>{t('common:chromeUi.devMode')}</span>
          <span aria-hidden="true" style={{ width: 34, height: 18, borderRadius: 20, position: 'relative', flex: '0 0 auto',
            background: devOn ? '#2d6a4f' : '#cbd5c3', transition: 'background .15s' }}>
            <span style={{ position: 'absolute', top: 2, left: devOn ? 18 : 2, width: 14, height: 14, borderRadius: '50%',
              background: '#fff', transition: 'left .15s' }} />
          </span>
        </button>
      </>}
    </nav>}

    {menu === 'create' && <section id="createMenu" className="menu open create-grid" style={pos} role="menu" aria-label={t('common:chromeUi.createRecord')} onKeyDown={moveInGrid}>
      <div className="m-lab eyebrow">{t('common:chromeUi.record')}</div>
      {CREATE_SECTIONS.map((sec, si) => <React.Fragment key={si}>
        {si > 0 && <div className="m-sep" />}
        {sec.items.map(([key, label, shortcut, i18nKey]) => <button key={key} type="button" role="menuitem" className={sec.full ? 'cg-full' : undefined} data-create={key}
          onClick={() => { close(); if (onCreate) onCreate(CREATE_TABS[key], CREATE_ROUTES[key], CREATE_PARAMS[key]); else if (CREATE_ROUTES[key]) navigate(`${CREATE_ROUTES[key]}?shell=v2`); }}>
          <span className="mi"><Glyph name={createGlyph(key)} /></span><span>{t(i18nKey)}</span><span className="mk">{shortcut}</span></button>)}
      </React.Fragment>)}
    </section>}

    {menu === 'bell' && <div id="bellPop" className="pop open" style={pos} role="menu" aria-label={t('common:chromeUi.notifications')}>
      <div className="m-lab eyebrow" style={{ padding: '6px 8px' }}>{t('common:chromeUi.notifications')}</div>
      {NOTES.map(([color, title, sub]) => <div className="p-row" key={title}><span className="pd" style={{ background: color }} /><div><div className="pt">{title}</div><div className="ps">{sub}</div></div></div>)}
    </div>}

    {menu === 'user' && <UserMenu apiInfo={apiInfo} offline={offline} style={pos} onClose={close} />}
  </header>;
}
